import { useNavigate } from "react-router-dom";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { PayslipListResponse, PayslipType } from "types/payroll-types";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import ManualPaginationTable from "components/ManualPaginationTable";
import { formatAmount } from "utils/utilityFunctions";
import { useState } from "react";
import Checkbox from "components/Checkbox";
import { toast } from "react-toastify";
import { payslipsStatusUpdateAPI } from "api/apiHelper";

interface PayslipsTableType {
  tableData: PayslipListResponse,
  tableName: string,
  ownPayslips?: boolean,
  pageLimit: number,
  currentPageNumber: number;
  getPayslipPage: (pageUrl: string, action: string) => void;
  getPayslipPageWithLimit: (pageSize: number) => void;
  getPayslips: () => void;
  handleSearch: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleDeletePayslip: () => void;
  openDeleteModal: (id: number) => void;
  closeDeleteModal: () => void;
  selectedPayslipId: number | null;
}

const PayslipsPaginatedTable = (props: PayslipsTableType) => {
  const { tableData, tableName, ownPayslips, pageLimit, currentPageNumber, getPayslipPage, getPayslipPageWithLimit, getPayslips, handleSearch, searchTerm, setSearchTerm, handleDeletePayslip, openDeleteModal, closeDeleteModal, selectedPayslipId } = props;
  const payslips_list = tableData.results;
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<PayslipType>();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("PAID")
  const [selectAll, setSelectAll] = useState(false);

  const handleRowClick = (payslip: PayslipType) => {
    navigate(ownPayslips ? `/my-payslip/${payslip.id}` : `/payslip/${payslip.id}`);
  };

  async function handlePayslipsStatusChange () {
      const notification = toast.loading("Creating Payslip Batch...");
      try {
        const response = await payslipsStatusUpdateAPI(selectedRows, selectedStatus);
        console.log("Generate Payslips Response: ", response.data);
        getPayslips();
        toast.update(notification, {
          render: "Payslips status changed successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
        // Update the table data to reflect the changes
  
      } catch (error) {
        toast.update(notification, {
          render: "Failed to update payslip status.",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
        console.log(error);
      }
    }

    const handleCheckboxChange = (row: any) => {
      const isSelected:Boolean = selectedRows.includes(row.original.id);
  
      if (isSelected) {
        setSelectedRows((prevSelectedRows) =>
          prevSelectedRows.filter((id:number) => id !== row.original.id)
        );
      } else {
        setSelectedRows((prevSelectedRows:number[]) => [...prevSelectedRows, row.original.id]);
      }
    };

    const handleSelectAll = () => {
        if (selectAll) {
          setSelectedRows([]);
        } else {
          const allIds:number[] = [];
          tableData.results.forEach((row:PayslipType) => {
            allIds.push(row.id);
          });
          setSelectedRows(allIds);
        }
        setSelectAll(!selectAll);
      };

  const columns:ColumnDef<PayslipType, any>[] = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => (
        <TableHeader>ID</TableHeader>
      ),
      cell: (info) => (
        <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
             checked={selectedRows.includes(info.row.original.id)}
             onChange={() => handleCheckboxChange(info.row)}
            me="10px"
          />
          <p className="ml-3 text-sm font-bold dark:text-blue-400">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnHelper.accessor((row) => `${row.employee_details.first_name} ${row.employee_details.last_name}`, {
      id: "name",
      header: () => <TableHeader>Name</TableHeader>,
      cell: (info) => <TableCell>{info.getValue()}</TableCell>,
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <TableHeader>Status</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("net_salary", {
      id: "net_salary",
      header: () => (
        <TableHeader>Net Salary</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {formatAmount(info.getValue())}
        </TableCell>
      ),
    }),
    columnHelper.accessor("period_start", {
      id: "period_start",
      header: () => (
        <TableHeader>Period Start</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor("period_end", {
      id: "period_end",
      header: () => (
        <TableHeader>Period End</TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => <TableHeader>Actions</TableHeader>,
      cell: (info) => {
        const payslipId = info.row.original.id;
        const status = info.row.original.status;

        return (
          <div className="flex gap-3 items-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(ownPayslips ? `/my-payslip/${payslipId}` : `/payslip/${payslipId}`);
              }}
              className="text-brand-500 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-200"
            >
              <i className="fas fa-eye h-5 w-5"></i>
            </button>
            {status.toUpperCase() === "DRAFT" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(payslipId);
              }}
              className="text-red-600 hover:text-red-800"
            >
              <i className="fas fa-trash h-5 w-5"></i>
            </button>
            )}
          </div>
        );
      },
    }),
  ];
  
  return (

<div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end">

        <div className=" flex-1 flex items-center gap-3 mt-2">
          {/* Add Search Input */}
          <input
              type="text"
              placeholder="Search payslips..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="border border-gray-300 rounded-lg px-3 py-1 w-60p"
            />
            <button
              onClick={handleSearch}
              className="bg-brand-500 rounded-lg text-white px-4 py-1  hover:bg-brand-600 me-16"
            >
              Search
            </button>
          <button
            onClick={handleSelectAll}
            className={`px-4 py-2 rounded-md text-sm font-medium md:mb-0 transition-colors ${
              selectAll
                ? "bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200"
                : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
            }`}
          >
            {!selectAll ? (
              <span className="flex items-center md:mb-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
                Select All
              </span>
            ) : (
              <span className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Deselect All
              </span>
            )}
          </button>

          <div className="flex items-center">
            <div className="relative inline-block">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="appearance-none bg-white border border-gray-300 rounded-l-md py-2 pl-3 pr-8 text-sm leading-tight focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                
              >
                <option value="PAID">PAID</option>
                <option value="DRAFT">DRAFT</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"></div>
            </div>
            <button
              onClick={handlePayslipsStatusChange}
              disabled={selectedRows.length === 0}
              className={`px-4 py-2 rounded-r-md text-sm font-medium ${
                selectedRows.length > 0
                  ? "bg-brand-500 flex gap-2 justify-between items-center  hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 p-3 text-[1rem] font-bold text-white mb-2 md:mb-0"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Go
            </button>
          </div>

          {selectedRows.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {selectedRows.length} selected
            </span>
          )}
        </div>
      </div>

    <ManualPaginationTable<PayslipType> tableData={payslips_list} columns={columns} tableHeading={tableName} totalRowCount={tableData.count}
        pageCount={pageLimit > 0? Math.ceil(tableData.count/pageLimit):1} pageRowsCount={pageLimit} previousUrl={tableData.previous} nextUrl={tableData.next}
        getPayslipPage={getPayslipPage} getPayslipPageWithLimit={getPayslipPageWithLimit} currentPageNumber={currentPageNumber} onRowClick={handleRowClick}
    />
    </div>
  )
}

export default PayslipsPaginatedTable;